import EditingBehaviorsBuilder from '../builder/EditingBehaviors.builder';
import StyleBuilder from '../builder/Style.builder';
import type { EditingBehaviors, WixComponentManifest, Preset, Style } from '../schema';
import componentManifestValidator from '../validator/componentManifestValidator';

class ComponentManifestBase {
  protected manifest: WixComponentManifest;

  constructor(manifest?: WixComponentManifest) {
    this.manifest = manifest || {};
  }

  public static validate(manifest: WixComponentManifest) {
    return componentManifestValidator(manifest);
  }

  public setDisplayName(displayName: string) {
    this.manifest.displayName = displayName;
    return this;
  }

  public getDisplayName() {
    return this.manifest.displayName;
  }

  public setSelector(selector: string) {
    this.manifest.selector = selector;
    return this;
  }

  public getSelector() {
    return this.manifest.selector;
  }

  public setReferredType(referredType?: string) {
    this.manifest.referredType = referredType;
    return this;
  }

  public getReferredType() {
    return this.manifest.referredType;
  }

  public setEditingBehaviors(editingBehaviors: EditingBehaviors | EditingBehaviorsBuilder) {
    if (!this.manifest.definitions) {
      this.manifest.definitions = {};
    }

    const isBuilder = editingBehaviors instanceof EditingBehaviorsBuilder;
    const behaviors = isBuilder ? editingBehaviors.build() : editingBehaviors;

    this.manifest.definitions.editingBehaviors = behaviors;
    return this;
  }

  public getEditingBehaviors() {
    return this.manifest.definitions?.editingBehaviors;
  }

  public setStyle(style: Style | StyleBuilder) {
    if (!this.manifest.definitions) {
      this.manifest.definitions = {};
    }

    const isBuilder = style instanceof StyleBuilder;
    const constructedStyle = isBuilder ? style.build() : style;

    this.manifest.definitions.style = constructedStyle;
    return this;
  }

  public getStyle() {
    return this.manifest.definitions?.style;
  }

  public getElements() {
    return this.manifest.elements;
  }

  public setElements(elements: Record<string, ComponentManifestBase | WixComponentManifest>) {
    Object.keys(elements).forEach((key) => {
      const element = elements[key];

      this.setElement(key, element);
    });

    return this;
  }

  public setElement(key: string, element: ComponentManifestBase | WixComponentManifest) {
    if (!this.manifest.elements) {
      this.manifest.elements = {};
    }

    this.manifest.elements[key] =
      element instanceof ComponentManifestBase ? element.getManifest() : element;
  }

  public setPresets(presets: Record<string, Preset>) {
    this.manifest.presets = presets;

    return this;
  }

  public getPresets() {
    return this.manifest.presets;
  }

  public getManifest() {
    return this.manifest;
  }

  public setManifest(manifest: WixComponentManifest) {
    this.manifest = manifest;
    return this;
  }
}

export default ComponentManifestBase;
